import React from "react";
import SEO from "../../components/common/SEO";
import {
  TrendingUp,
  Target,
  Mail,
  BarChart3,
  Zap,
  CheckCircle,
  ArrowRight,
  Users,
  DollarSign,
  Sparkles,
} from "lucide-react";

export default function Marketing({ user, authToken }) {
  return (
    <>
      <SEO
        title="Email Validation for Marketers - VALIDMAIL"
        description="Boost deliverability, open rates, and campaign ROI. Real-time validation, list cleaning, and risk scoring for email marketers."
        keywords="email marketing validation, deliverability, open rate optimization, email list cleaning"
        canonicalUrl="https://validmail.io/solutions/marketing"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              For Email Marketers
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-4 sm:mb-6">
              Send email with{" "}
              <span className="text-sky-600 dark:text-sky-400">confidence</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Boost deliverability, protect your sender reputation, and maximize campaign ROI with real-time validation and intelligent list cleaning.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-sky-200 dark:border-sky-800 text-center">
              <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-sky-600 dark:text-sky-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">98%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Deliverability</p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-200 dark:border-emerald-800 text-center">
              <Mail className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">-54%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Bounce Rate</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200 dark:border-purple-800 text-center">
              <BarChart3 className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600 dark:text-purple-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">+22%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Open Rates</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-amber-200 dark:border-amber-800 text-center">
              <DollarSign className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 dark:text-amber-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">3x</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Campaign ROI</p>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              How VALIDMAIL fits your workflow
            </h2>
            <div className="relative">
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-sky-200 via-sky-400 to-sky-200 dark:from-sky-800 dark:via-sky-600 dark:to-sky-800 -translate-y-1/2"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-4 relative">
                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-sky-200 dark:border-sky-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    1
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Capture</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Collect emails from forms, landing pages, and CRMs
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-sky-200 dark:border-sky-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    2
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Validate</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Real-time checks for syntax, domain, mailbox, and spam traps
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-sky-200 dark:border-sky-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    3
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Nurture</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Segment clean contacts and build engagement workflows
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-sky-200 dark:border-sky-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    4
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Send</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Launch campaigns knowing every address is verified
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-sky-200 dark:border-sky-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    5
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Optimize</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Track deliverability metrics and refine targeting
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              Built for marketing teams
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-sky-200 dark:border-sky-800">
                <Target className="h-10 w-10 sm:h-12 sm:w-12 text-sky-600 dark:text-sky-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Protect Sender Reputation
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Reduce hard bounces, avoid spam complaints, and maintain stable IP/domain health with proactive validation.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Catch invalid emails before they damage your score</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Detect spam traps and abuse addresses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Maintain consistent inbox placement rates</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-emerald-200 dark:border-emerald-800">
                <TrendingUp className="h-10 w-10 sm:h-12 sm:h-12 text-emerald-600 dark:text-emerald-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Make Every Campaign Count
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Higher inbox placement means more opens, clicks, and revenue per send. Stop wasting budget on bad emails.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Increase open rates by 22% on average</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Improve click-through rates and conversions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Lower cost per acquisition with clean lists</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800">
                <Zap className="h-10 w-10 sm:h-12 sm:w-12 text-purple-600 dark:text-purple-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Real-time Lead Validation
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Stop bad emails at the source. Validate leads instantly as they sign up through forms and landing pages.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">JavaScript SDK for instant form validation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Catch typos and disposable emails live</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Build clean lists from day one</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-amber-200 dark:border-amber-800">
                <Users className="h-10 w-10 sm:h-12 sm:w-12 text-amber-600 dark:text-amber-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Smart List Segmentation
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Use risk scoring to create targeted segments. Send to safe contacts, nurture risky ones, and suppress dangerous addresses.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Low, medium, high risk classification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Export clean segments to your ESP</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Automate suppression rules</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-2xl sm:rounded-3xl p-8 sm:p-12 border-2 border-slate-200 dark:border-slate-700 mb-16 md:mb-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-4">
                Case Study
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
                E-commerce brand increases revenue by 3x
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-6">
                A growing e-commerce company was struggling with poor email deliverability and low engagement rates. After integrating VALIDMAIL's real-time validation on their checkout forms and cleaning their existing list of 50,000 contacts:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">54%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Reduction in bounce rate</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">22%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Increase in open rates</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">3x</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Campaign ROI improvement</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-sky-600 to-blue-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Ready to boost your campaigns?
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
              Start with 100 free validation credits. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => window.location.href = authToken ? "/product/email-verification" : "/signin"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold bg-white text-sky-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
              >
                {authToken ? "Start Validating" : "Get Started Free"}
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => window.location.href = "/pricing"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold border-2 border-white text-white hover:bg-white hover:text-sky-600 transition-all"
              >
                View Pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
